// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
// eslint-disable-next-line no-restricted-imports
import { jsx, ParamComponent } from 'panel';
import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { VNode } from 'snabbdom';
import type { JsxVNodeProps } from 'snabbdom-jsx-lite';
import { IronContextRegistry } from 'common/report/context-registry';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { ReactErrorBoundary } from 'common/components/react-error-boundary';
import { getReactComponentId } from './react';
interface Params {
  props: any;
}
interface JsxProps {
  params: Params;
}
type Options = {
  /** Optional styles to add to the wrapper "content" div */
  wrapperStyles?: Record<string, string>;
  /**
   * Use the shadow dom.
   *
   * WARNING: Do not disable this unless you have removed host styles from your component. It might affect other components!
   */
  useShadowDom?: boolean;
};
interface PanelProps {
  styleOverride?: string;
}

/**
 * Wraps a React component with a Panel component.
 */
export function react2panel<Component extends React.JSXElementConstructor<any>>(name: string, reactComponent: Component, opts?: Options): (props: Pick<JsxVNodeProps, `sel` | `hook` | `class`> & PanelProps & React.ComponentPropsWithRef<Component>) => VNode {
  const componentId = (reactComponent as any).componentId || getReactComponentId();
  class ReactRoot extends ParamComponent<Params, unknown, IronContextRegistry> {
    declare private rootEl: HTMLElement;
    declare private reactRoot: ReactDOM.Root;
    get config() {
      return {
        params: {
          props: Object
        },
        useShadowDom: opts?.useShadowDom,
        template: () => <div style={{
          // make sure the root isn't causing any styling changes
          display: `contents`,
          ...(opts?.wrapperStyles ? opts.wrapperStyles : {})
        }} hook={{
          insert: vnode => {
            this.rootEl = vnode.elm as HTMLElement;
          }
        }} />,
        hooks: {
          postUpdate: () => this.render()
        }
      };
    }

    /**
     * Renders our React component.
     */
    render() {
      if (!this.isConnected) {
        return;
      }
      const props = this.params.props;

      // add contexts to props
      let element: React.ReactNode = React.createElement(reactComponent, {
        ...props
      });

      // wrap with error boundary
      element = React.createElement(ReactErrorBoundary, {
        children: element,
        minimal: true
      });
      this.reactRoot.render(element);
    }
    connectedCallback(): void {
      super.connectedCallback();

      // set up the react root
      this.reactRoot = ReactDOM.createRoot(this.rootEl);
      this.render();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      this.reactRoot.unmount();
    }
  }
  const panelTagName = `${name}-${componentId}`;
  const ReactRootEl = defineAndMakeJsxFactory<JsxProps>(panelTagName, ReactRoot);
  const Component = props => {
    const {
      sel,
      hook,
      ...rest
    } = props ?? {};
    return <ReactRootEl sel={sel} hook={hook} params={{
      props: rest
    }} data-sentry-element="ReactRootEl" data-sentry-component="Component" data-sentry-source-file="react2panel-nocontext.tsx" />;
  };

  /** Accesses the Panel wrapper's tag name. ONLY rely on this for legacy use cases. */
  Component.panelTagName = panelTagName;
  return Component;
}